import type { CollageData } from '../interfaces/collage-data.interface';
import type { DrawableData } from '../interfaces/drawable-data.interface';

/**
 * База клиппера у drawable v0 — тот же рецепт запечки, что у бокса (`clipBaseIdOf`), в `templateMeta`. Id листьев
 * лежат в пространстве drawable: `generateDrawablesFromBoxes` перенумеровывает их вместе с боксами одного применения.
 */
export function drawableClipBaseIdOf(drawable: DrawableData): string | undefined {
  return drawable.type === 'object' ? drawable.templateMeta?.compoundClipBakeRecipe?.leafBoxIds[0] : undefined;
}

/**
 * Effective editor lock of one album drawable — the photobook twin of `isBoxLocked` for design packs.
 * Structural on purpose: the layers panel, the edit ops and the render adapter all resolve the default
 * through this one rule, so a padlock can never show open on a drawable the canvas already refuses.
 * The background is scenery and locks by default; an explicit flag (the padlock the user clicked) wins.
 */
export function isDrawableLocked(drawable: { type: string; locked?: boolean }): boolean {
  return drawable.locked ?? drawable.type === 'background';
}

/**
 * Every drawable a geometry writer must refuse — a locked group's whole subtree included. The album twin
 * of `lockedBoxIds` for design packs: the sink gate, the layers panel and the context menu all read the
 * same walk, so a padlock can never mean one thing in the panel and another on the canvas.
 */
export function lockedDrawableIds(collages: readonly CollageData[]): ReadonlySet<string> {
  const ids = new Set<string>();
  walkLocks(collages, (id, locked) => {
    if (locked) ids.add(id);
  });
  return ids;
}

/**
 * Only the drawables whose lock is handed down by an enclosing group — their row shows a padlock they
 * cannot release on their own. The album twin of `ancestorLockedBoxIds`.
 */
export function ancestorLockedDrawableIds(collages: readonly CollageData[]): ReadonlySet<string> {
  const ids = new Set<string>();
  walkLocks(collages, (id, _locked, inherited) => {
    if (inherited) ids.add(id);
  });
  return ids;
}

function walkLocks(collages: readonly CollageData[], visit: (id: string, locked: boolean, inherited: boolean) => void): void {
  const locked = new Set<string>();
  const walk = (drawables: readonly DrawableData[], inherited: boolean): void => {
    for (const d of drawables) {
      const own = inherited || isDrawableLocked(d);
      if (own) locked.add(d.id);
      visit(d.id, own, inherited);
      if (d.type === 'group') walk(d.drawableObjects, own);
    }
  };

  for (const collage of collages) walk(collage.drawableObjects, false);

  // A clipper locks with its base, wherever on the spread it sits (box-tree lockedClipperIdsInBoxTrees): the base's
  // lock counts as an ancestor's, so the clipper cannot release it alone. Passes repeat to a fixed point, so a chain
  // «clipper of a clipper» locks whatever the collage order.
  const joined = new Set<string>();
  let grew = true;
  const clippers = (drawables: readonly DrawableData[]): void => {
    for (const d of drawables) {
      const base = drawableClipBaseIdOf(d);
      if (base !== undefined && base !== d.id && !joined.has(d.id) && locked.has(base)) {
        joined.add(d.id);
        locked.add(d.id);
        visit(d.id, true, true);
        grew = true;
      }

      if (d.type === 'group') clippers(d.drawableObjects);
    }
  };

  while (grew) {
    grew = false;
    for (const collage of collages) clippers(collage.drawableObjects);
  }
}
